import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconMore, IconPin, IconArchive } from './icons';
import type { ConversationSummary, ProjectSummary } from '../../lib/conversationsApi';

export type ConversationAction = { type: 'pin' } | { type: 'archive' } | { type: 'delete' } | { type: 'share' } | { type: 'rename'; title: string } | { type: 'move'; projectId: string | null };
export function ConversationActions({ conversation, projects, onAction, quick = false }: {
  conversation: ConversationSummary; projects: ProjectSummary[];
  onAction: (action: ConversationAction) => void | Promise<void>; quick?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<'rename' | 'delete' | null>(null);
  const [title, setTitle] = useState(conversation.title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const close = () => { setOpen(false); setEditing(null); trigger.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('button, input')?.focus();
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); close(); } };
    const outside = (event: MouseEvent) => { if (!panel.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('keydown', key); document.addEventListener('mousedown', outside);
    return () => { document.removeEventListener('keydown', key); document.removeEventListener('mousedown', outside); };
  }, [open]);
  const run = async (action: ConversationAction) => {
    if (busy) return;
    setBusy(true); setError('');
    try { await onAction(action); close(); }
    catch { setError('Could not update this conversation. Please try again.'); }
    finally { setBusy(false); }
  };
  const pin = conversation.pinned ? 'Unpin' : 'Pin';
  const archive = conversation.archived ? 'Unarchive' : 'Archive';
  return <span className="ozira-conversation-actions" data-open={open}>
    {quick && <>
      <button type="button" title={pin} aria-label={`${pin} ${conversation.title}`} aria-pressed={conversation.pinned} disabled={busy} onClick={() => void run({ type: 'pin' })}><IconPin /></button>
      <button type="button" title={archive} aria-label={`${archive} ${conversation.title}`} disabled={busy} onClick={() => void run({ type: 'archive' })}><IconArchive /></button>
    </>}
    <button ref={trigger} type="button" title="Conversation actions" aria-label={`Actions for ${conversation.title}`} aria-expanded={open} onClick={() => {
      const box = trigger.current!.getBoundingClientRect();
      setPosition({ top: Math.max(8, Math.min(box.bottom + 4, window.innerHeight - 360)), left: Math.max(8, Math.min(box.left, window.innerWidth - 272)) });
      setEditing(null); setError(''); setTitle(conversation.title); setOpen(!open);
    }}><IconMore /></button>
    {error && !open && <span role="alert" className="ozira-row-error">{error}</span>}
    {open && createPortal(<div ref={panel} role="dialog" aria-label={`Actions for ${conversation.title}`} className="ozira-conversation-action-menu" style={position}>
      {editing === 'rename' ? <form onSubmit={event => { event.preventDefault(); if (title.trim()) void run({ type: 'rename', title: title.trim() }); }}>
        <label>Conversation name<input autoFocus value={title} onChange={event => setTitle(event.target.value)} /></label>
        <button disabled={busy || !title.trim()} type="submit">Save name</button><button type="button" onClick={close}>Cancel</button>
      </form> : editing === 'delete' ? <>
        <p>Delete “{conversation.title}”? This cannot be undone.</p>
        <button disabled={busy} onClick={() => void run({ type: 'delete' })}>Confirm delete</button><button onClick={close}>Cancel</button>
      </> : <>
        <button disabled={busy} onClick={() => setEditing('rename')}>Rename</button>
        <button disabled={busy} onClick={() => void run({ type: 'pin' })}>{pin}</button>
        <button disabled={busy} onClick={() => void run({ type: 'archive' })}>{archive}</button>
        <button disabled={busy} onClick={() => void run({ type: 'share' })}>Share</button>
        <label>Project<select aria-label="Move to project" value={conversation.projectId ?? ''} disabled={busy} onChange={event => void run({ type: 'move', projectId: event.target.value || null })}>
          <option value="">No project</option>{projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select></label>
        <button disabled={busy} onClick={() => setEditing('delete')}>Delete</button>
      </>}
      {error && <p role="alert">{error}</p>}
    </div>, document.body)}
  </span>;
}
